// node modules
import React from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useSelector } from 'react-redux';
import { gql, useQuery, useMutation, useApolloClient } from '@apollo/client';
import PropTypes from 'prop-types';
import { SearchkitComponent } from 'searchkit';
import classNames from 'classnames';
import { isApp } from 'services/environment';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

// app modules: components
import Button from 'components/Button';
import Icon from 'components/Icon';
import SearchkitDropdown from 'components/SearchkitDropdown';

// constants
import { FILTERS } from 'constants/search';
import { BREAKPOINTS } from 'constants/index';

// assets
import CssSearchFilters from './SearchFilters.scss';

export const GQL = {
  GET_SETTINGS: gql`
    query GetSearchFilterSettings {
      User {
        id
        settings {
          id
          search_filters_visible
        }
      }
    }
  `,
  SAVE_SETTINGS: gql`
    mutation UpdateSearchFilterSettings($settings: SettingsInput) {
      UpdateUserSettings2(settings: $settings) {
        id
        search_filters_visible
      }
    }
  `,
};

const ORDER = [
  '2010-present day',
  '2000s',
  '1980s & 1990s',
  '1960s & 1970s',
  'Early 20th Century',
  'Pre-20th Century',
  'Easy',
  'Medium',
  'Complex',
  'Ready in 30 minutes',
  '5 ingredients max',
];

export const sortEraFields = buckets => {
  const results = new Array(ORDER.length);

  buckets.forEach(bucket => {
    const index = ORDER.indexOf(bucket.key);

    if (index !== -1) {
      results[index] = bucket;
    }
  });

  return results.filter(bucket => typeof bucket !== 'undefined');
};

export function SearchFilters(props) {
  const { isVisible, useFullBookTitle, style, className } = props;
  useStyles(CssSearchFilters);
  const dimensions = useSelector(state => state.dimensions);
  const searchIsEmpty = useSelector(state => state.search.isEmpty);
  const loggedIn = useSelector(state => state.user.logged_in);
  const userId = useSelector(state => state.user.id);
  const [setFilterVisibility] = useMutation(GQL.SAVE_SETTINGS);
  const client = useApolloClient();
  const { data } = useQuery(GQL.GET_SETTINGS, {
    skip: !isVisible || (loggedIn && userId <= 0),
    ssr: false,
    _authenticated: true,
    fetchPolicy: isApp() ? 'cache-and-network' : 'cache-first',
  });

  let filtersVisibleInMobileView = searchIsEmpty;
  if (data && data.User && typeof data.User.settings.search_filters_visible === 'boolean') {
    filtersVisibleInMobileView = data.User.settings.search_filters_visible;
  }

  const isWideDisplay = dimensions.body > BREAKPOINTS.PHONE_VERTICAL;
  const displayInactiveFilters = isWideDisplay || filtersVisibleInMobileView;

  const searchFilterButtonClassName = classNames('search-filters__button', {
    'margin-bottom-5': filtersVisibleInMobileView,
  });

  const toggleFilters = () => {
    const newValue = !filtersVisibleInMobileView;
    if (loggedIn && userId > 0) {
      // When logged in we save the state in the user's settings
      setFilterVisibility({
        variables: { settings: { search_filters_visible: newValue } },
        optimisticResponse: {
          UpdateUserSettings2: {
            __typename: 'Settings',
            id: userId,
            search_filters_visible: newValue,
          },
        },
      });
    } else {
      // When not logged in we save the state in the local apollo cache
      client.writeQuery({
        query: GQL.GET_SETTINGS,
        data: {
          User: {
            id: null,
            settings: {
              id: null,
              search_filters_visible: newValue,
            },
          },
        },
      });
    }
  };

  return (
    <div
      data-cy="search-filters"
      className={`search-filters ${className}`}
      hidden={!isVisible}
      style={style}
    >
      <div
        onClick={toggleFilters}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            toggleFilters();
          }
        }}
        aria-label={filtersVisibleInMobileView ? 'hide filters' : 'show filters'}
        className={searchFilterButtonClassName}
      >
        <Button themeName="dusty" themeType="clear" size="large">
          <Icon
            className="search-filters__filter"
            name="filter"
            fill="currentColor"
            height="20"
            width="20"
          />{' '}
        </Button>

        <ChevronRightIcon
          fontSize="medium"
          style={{
            transform: filtersVisibleInMobileView ? 'rotate(-90deg)' : 'rotate(90deg)',
          }}
          className="search-filters__button__chevron-icon"
        />
      </div>

      <div className="search-filters__body">
        <SearchkitDropdown
          id="country"
          field="country.raw"
          stateKey={FILTERS.COUNTRY}
          title="Cuisine"
          operator="OR"
          visibleWhenUnselected={displayInactiveFilters}
        />
        <SearchkitDropdown
          id="course"
          field="course.raw"
          stateKey={FILTERS.COURSE}
          title="Course"
          operator="OR"
          visibleWhenUnselected={displayInactiveFilters}
        />
        <SearchkitDropdown
          id="diet_type"
          field="diet_type.raw"
          stateKey={FILTERS.DIET_TYPE}
          title="Diet"
          operator="AND"
          visibleWhenUnselected={displayInactiveFilters}
        />
        <SearchkitDropdown
          id="skill"
          field="skill.raw"
          stateKey={FILTERS.SKILL}
          title="Complexity"
          operator="OR"
          visibleWhenUnselected={displayInactiveFilters}
          bucketsTransform={sortEraFields}
        />
        <SearchkitDropdown
          id="era"
          field="era.raw"
          stateKey={FILTERS.ERA}
          title="Era"
          operator="OR"
          visibleWhenUnselected={displayInactiveFilters}
          bucketsTransform={sortEraFields}
        />
        <SearchkitDropdown
          id="authors"
          field="authors.raw"
          stateKey={FILTERS.AUTHORS}
          title="Authors"
          operator="OR"
          visibleWhenUnselected={displayInactiveFilters}
        />
        <SearchkitDropdown
          id="book_title"
          field="book_title.raw"
          stateKey={FILTERS.BOOK_TITLE}
          title="Book"
          operator="OR"
          visibleWhenUnselected={displayInactiveFilters}
          hidden={useFullBookTitle}
        />
        <SearchkitDropdown
          id="book_full_title"
          field="book_full_title.raw"
          stateKey={FILTERS.BOOK_FULL_TITLE}
          title="Book"
          operator="OR"
          visibleWhenUnselected={displayInactiveFilters}
          hidden={!useFullBookTitle}
        />
      </div>
    </div>
  );
}

SearchFilters.propTypes = {
  isVisible: PropTypes.bool,
  useFullBookTitle: PropTypes.bool,
  style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  className: PropTypes.string,
};

SearchFilters.defaultProps = {
  isVisible: true,
  useFullBookTitle: false,
  style: {},
  className: '',
};

export class SearchFiltersWrapper extends SearchkitComponent {
  static propTypes = {
    isVisible: PropTypes.bool,
    style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    className: PropTypes.string,
  };

  static defaultProps = {
    isVisible: true,
    style: {},
    className: '',
  };

  render() {
    const { isVisible, style, className } = this.props;

    // Normally the book filter uses short title. Sometimes we need to use full title.
    const useFullBookTitle =
      this.searchkit &&
      this.searchkit.state.book_full_title &&
      this.searchkit.state.book_full_title.length > 0;

    return (
      <SearchFilters
        isVisible={isVisible}
        useFullBookTitle={useFullBookTitle}
        className={className}
        style={style}
      />
    );
  }
}

export default SearchFiltersWrapper;
